/** The default RecorderApi: talks to the console (docs/recorder-api.md). Token routes send only the bearer token (no
 * cookie, `credentials: "omit"`); member routes send the console cookie and the CSRF token the app supplies. */
import { ApiFailure } from "./types.js";
import type { ChunkRequest, RecorderApi, ResumeResponse, SignedPut, StopRequest, StopResponse, UnsentList } from "./types.js";

async function parse<T>(res: Response): Promise<T> {
  let body: { error?: string; message?: string } & Record<string, unknown> = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* an empty body: the status says it */
  }
  if (!res.ok) throw new ApiFailure(res.status, body.error ?? "failed", body.message ?? "");
  return body as T;
}

export function httpRecorderApi(getCsrf: () => string | Promise<string>, fetchImpl: typeof fetch = fetch): RecorderApi {
  const bearer = async <T>(token: string, path: string, body?: object): Promise<T> =>
    parse<T>(
      await fetchImpl(path, {
        method: "POST",
        credentials: "omit", // the recording token alone: an upload must not depend on the console cookie
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body ?? {}),
      }),
    );
  const member = async <T>(path: string, body?: object): Promise<T> =>
    parse<T>(
      await fetchImpl(path, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": await getCsrf() },
        body: JSON.stringify(body ?? {}),
      }),
    );
  return {
    chunkUrl: (token, id, body: ChunkRequest) => bearer<SignedPut>(token, `/api/recorder/${id}/chunk-url`, body),
    stop: (token, id, body: StopRequest) => bearer<StopResponse>(token, `/api/recorder/${id}/stop`, body),
    complete: (token, id) => bearer<StopResponse>(token, `/api/recorder/${id}/complete`),
    pause: (token, id, body) => bearer(token, `/api/recorder/${id}/pause`, body),
    continueRecording: (token, id) => bearer(token, `/api/recorder/${id}/continue`),
    diagnostics: async (token, id, body) => {
      await bearer(token, `/api/recorder/${id}/diagnostics`, body);
    },
    async put(url, headers, body) {
      const res = await fetchImpl(url, { method: "PUT", headers, body: body as BodyInit, credentials: "omit" });
      return res.status;
    },
    async unsent() {
      const res = await fetchImpl("/api/recorder/unsent", { credentials: "same-origin" });
      return parse<UnsentList>(res);
    },
    resume: (id) => member<ResumeResponse>(`/api/recorder/${id}/resume`),
    async discard(id, keepUploaded) {
      await member(`/api/recorder/${id}/discard`, { confirm: true, keep_uploaded: keepUploaded });
    },
  };
}
